import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ComposePost from "@/components/ComposePost";
import PostCard from "@/components/PostCard";
import CommunityAiCard from "@/components/CommunityAiCard";
import { ArrowLeft, Users, Target, CalendarDays, BookOpen, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const blankGoal = { title: "", description: "", target: "", unit: "", due_at: "" };
const blankEvent = { title: "", description: "", starts_at: "", ends_at: "", location_label: "", external_url: "" };
const blankResource = { title: "", description: "", resource_type: "link", url: "" };

export default function CommunityDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  if (!user?.id || !id) return null;
  return <CommunityDetail key={JSON.stringify([user.id, id])} id={id} />;
}

function CommunityDetail({ id }) {
  const [comm, setComm] = useState(null);
  const [posts, setPosts] = useState([]);
  const [hub, setHub] = useState(null);
  const [goal, setGoal] = useState(blankGoal);
  const [event, setEvent] = useState(blankEvent);
  const [resource, setResource] = useState(blankResource);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [hubLoading, setHubLoading] = useState(false);
  const [hubError, setHubError] = useState(false);
  const mounted = useRef(false);
  const loadVersion = useRef(0);
  const hubVersion = useRef(0);

  const loadHub = useCallback(async () => {
    const version = ++hubVersion.current;
    setHub(null);
    setHubLoading(true);
    setHubError(false);
    try {
      const r = await api.get(`/communities/${id}/hub`, { timeout: 30000 });
      if (!mounted.current || version !== hubVersion.current) return;
      if (!r.data || !Array.isArray(r.data.goals) || !Array.isArray(r.data.events) || !Array.isArray(r.data.resources)) throw new Error("Invalid hub response");
      setHub(r.data);
    } catch {
      if (mounted.current && version === hubVersion.current) setHubError(true);
    } finally {
      if (mounted.current && version === hubVersion.current) setHubLoading(false);
    }
  }, [id]);

  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError(false);
    try {
      const [c, p] = await Promise.all([
        api.get(`/communities/${id}`, { timeout: 30000 }),
        api.get(`/posts/feed?community_id=${id}`, { timeout: 30000 }),
      ]);
      if (!mounted.current || version !== loadVersion.current) return;
      if (c.data?.id !== id || !Array.isArray(p.data)) throw new Error("Invalid community response");
      setComm(c.data);
      setPosts(p.data);
      if (c.data.joined) await loadHub();
      else {
        hubVersion.current += 1;
        setHub(null);
        setHubError(false);
        setHubLoading(false);
      }
    } catch {
      if (mounted.current && version === loadVersion.current) setLoadError(true);
    } finally {
      if (mounted.current && version === loadVersion.current) setLoading(false);
    }
  }, [id, loadHub]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  if (loading) return <div role="status" className="brutal-card p-5 bg-card">Loading…</div>;
  if (loadError || !comm) return <div role="alert" className="brutal-card p-5 bg-card">
    Could not load this community.
    <button onClick={load} data-testid="retry-community-detail" className="brutal-btn px-3 py-1 ml-3">Retry</button>
  </div>;

  const join = async () => {
    try {
      const r = await api.post(`/communities/${id}/join`, undefined, { timeout: 30000 });
      if (!mounted.current) return;
      setComm({ ...comm, joined: r.data.joined, members_count: r.data.members_count });
      toast.success(r.data.joined ? "Joined" : "Left");
      if (r.data.joined) await loadHub();
      else {
        hubVersion.current += 1;
        setHub(null);
        setHubError(false);
        setHubLoading(false);
      }
    } catch (e) {
      if (mounted.current) toast.error(e.response?.data?.detail || "Could not update membership");
    }
  };

  const createGoal = async () => {
    if (!goal.title.trim()) return;
    setBusy("goal");
    try {
      await api.post(`/communities/${id}/goals`, {
        ...goal,
        target: goal.target ? Number(goal.target) : null,
        due_at: goal.due_at || null,
      }, { timeout: 30000 });
      if (!mounted.current) return;
      setGoal(blankGoal);
      await loadHub();
      if (mounted.current) toast.success("Goal created");
    } catch (e) { if (mounted.current) toast.error(e.response?.data?.detail || "Could not create goal"); }
    finally { if (mounted.current) setBusy(""); }
  };

  const updateProgress = async (item) => {
    const raw = window.prompt(`Update progress for ${item.title}`, String(item.progress || 0));
    if (raw === null) return;
    const progress = Number(raw);
    if (!Number.isFinite(progress) || progress < 0) return toast.error("Enter a valid progress number");
    try {
      await api.put(`/communities/${id}/goals/${item.id}/progress`, { progress }, { timeout: 30000 });
      if (!mounted.current) return;
      await loadHub();
      if (mounted.current) toast.success("Progress updated");
    } catch (e) { if (mounted.current) toast.error(e.response?.data?.detail || "Could not update goal"); }
  };

  const createEvent = async () => {
    if (!event.title.trim() || !event.starts_at) return toast.error("Add a title and start time");
    setBusy("event");
    try {
      await api.post(`/communities/${id}/events`, {
        ...event,
        ends_at: event.ends_at || null,
        external_url: event.external_url || null,
      }, { timeout: 30000 });
      if (!mounted.current) return;
      setEvent(blankEvent);
      await loadHub();
      if (mounted.current) toast.success("Event created");
    } catch (e) { if (mounted.current) toast.error(e.response?.data?.detail || "Could not create event"); }
    finally { if (mounted.current) setBusy(""); }
  };

  const rsvp = async (eventId) => {
    try {
      await api.post(`/communities/${id}/events/${eventId}/rsvp`, undefined, { timeout: 30000 });
      if (!mounted.current) return;
      await loadHub();
    } catch (e) { if (mounted.current) toast.error(e.response?.data?.detail || "Could not update RSVP"); }
  };

  const createResource = async () => {
    if (!resource.title.trim()) return;
    setBusy("resource");
    try {
      await api.post(`/communities/${id}/resources`, { ...resource, url: resource.url || null }, { timeout: 30000 });
      if (!mounted.current) return;
      setResource(blankResource);
      await loadHub();
      if (mounted.current) toast.success("Resource added");
    } catch (e) { if (mounted.current) toast.error(e.response?.data?.detail || "Could not add resource"); }
    finally { if (mounted.current) setBusy(""); }
  };

  return (
    <div data-testid="community-detail" className="space-y-6">
      <Link to="/communities" className="brutal-btn bg-card px-3 py-1 text-sm inline-flex items-center gap-2 mb-4"><ArrowLeft size={14} strokeWidth={2.5} /> Back</Link>
      <div className="brutal-card bg-sage p-6">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <span className="text-xs uppercase tracking-wider bg-card border-2 border-foreground px-2 py-0.5 rounded">{comm.category}</span>
            <h1 className="text-4xl mt-2">{comm.name}</h1>
            <p className="mt-2 max-w-3xl">{comm.description}</p>
            <div className="mt-3 text-sm flex items-center gap-2"><Users size={14} strokeWidth={2.5} /> {comm.members_count} members</div>
          </div>
          <button onClick={join} data-testid="comm-detail-join"
            className={`brutal-btn px-4 py-2 ${comm.joined ? "bg-foreground text-background" : "bg-primary text-primary-foreground"}`}>
            {comm.joined ? (comm.is_owner ? "Owner" : "Joined") : "Join"}
          </button>
        </div>
      </div>

      {comm.joined && hubLoading && <p role="status">Loading collaboration hub…</p>}
      {comm.joined && hubError && <div role="alert" className="brutal-card p-5 bg-card">
        Could not load the collaboration hub.
        <button onClick={loadHub} data-testid="retry-community-hub" className="brutal-btn px-3 py-1 ml-3">Retry</button>
      </div>}
      {comm.joined && hub && (
        <section className="space-y-4" data-testid="community-hub">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold opacity-70">Collaboration hub</div>
              <h2 className="text-3xl">Turn conversation into outcomes</h2>
            </div>
            <div className="flex gap-2 text-xs flex-wrap">
              <span className="brutal-card bg-card px-3 py-2">{hub.outcomes?.open_goals || 0} open goals</span>
              <span className="brutal-card bg-card px-3 py-2">{hub.outcomes?.completed_goals || 0} completed</span>
              <span className="brutal-card bg-card px-3 py-2">{hub.outcomes?.upcoming_events || 0} events</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <div className="brutal-card bg-card p-4">
              <h3 className="text-xl flex items-center gap-2"><Target size={18} /> Goals</h3>
              <div className="space-y-3 mt-3">
                {(hub.goals || []).length === 0 && <p className="text-sm text-muted-foreground">No shared goals yet.</p>}
                {(hub.goals || []).map((g) => {
                  const pct = g.target ? Math.min(100, Math.round(((g.progress || 0) / g.target) * 100)) : null;
                  return <div key={g.id} className="border-2 border-foreground rounded-xl p-3">
                    <div className="font-bold">{g.title}</div>
                    {g.description && <p className="text-sm mt-1">{g.description}</p>}
                    <div className="text-xs mt-2">{g.progress || 0}{g.target ? ` / ${g.target}` : ""} {g.unit || ""} · {g.status}</div>
                    {pct !== null && <div className="h-2 mt-2 border border-foreground rounded overflow-hidden"><div className="h-full bg-primary" style={{ width: `${pct}%` }} /></div>}
                    {hub.is_owner && <button onClick={() => updateProgress(g)} className="text-xs underline mt-2">Update progress</button>}
                  </div>;
                })}
              </div>
              {hub.is_owner && <div className="mt-4 space-y-2 border-t-2 border-foreground pt-3">
                <input className="brutal-input w-full px-3 py-2" placeholder="Goal title" value={goal.title} onChange={(e) => setGoal({ ...goal, title: e.target.value })} />
                <textarea className="brutal-input w-full px-3 py-2" placeholder="What will this accomplish?" value={goal.description} onChange={(e) => setGoal({ ...goal, description: e.target.value })} />
                <div className="grid grid-cols-2 gap-2"><input className="brutal-input px-3 py-2" type="number" placeholder="Target" value={goal.target} onChange={(e) => setGoal({ ...goal, target: e.target.value })} /><input className="brutal-input px-3 py-2" placeholder="Unit" value={goal.unit} onChange={(e) => setGoal({ ...goal, unit: e.target.value })} /></div>
                <button onClick={createGoal} disabled={busy === "goal"} className="brutal-btn bg-primary text-primary-foreground px-3 py-2 w-full flex justify-center gap-2"><Plus size={14} /> Add goal</button>
              </div>}
            </div>

            <div className="brutal-card bg-card p-4">
              <h3 className="text-xl flex items-center gap-2"><CalendarDays size={18} /> Events</h3>
              <div className="space-y-3 mt-3">
                {(hub.events || []).length === 0 && <p className="text-sm text-muted-foreground">No events scheduled yet.</p>}
                {(hub.events || []).map((ev) => <div key={ev.id} className="border-2 border-foreground rounded-xl p-3">
                  <div className="font-bold">{ev.title}</div>
                  <div className="text-xs mt-1">{ev.starts_at}{ev.location_label ? ` · ${ev.location_label}` : ""}</div>
                  {ev.description && <p className="text-sm mt-2">{ev.description}</p>}
                  <div className="flex gap-2 mt-2 items-center flex-wrap"><button onClick={() => rsvp(ev.id)} className={`brutal-btn px-2 py-1 text-xs ${ev.attending ? "bg-foreground text-background" : "bg-card"}`}>{ev.attending ? "Going" : "RSVP"} · {ev.attendee_count}</button>{ev.external_url && <a href={ev.external_url} target="_blank" rel="noreferrer" className="text-xs underline inline-flex gap-1">Details <ExternalLink size={11} /></a>}</div>
                </div>)}
              </div>
              {hub.is_owner && <div className="mt-4 space-y-2 border-t-2 border-foreground pt-3">
                <input className="brutal-input w-full px-3 py-2" placeholder="Event title" value={event.title} onChange={(e) => setEvent({ ...event, title: e.target.value })} />
                <input className="brutal-input w-full px-3 py-2" type="datetime-local" value={event.starts_at} onChange={(e) => setEvent({ ...event, starts_at: e.target.value })} />
                <input className="brutal-input w-full px-3 py-2" placeholder="Location or online" value={event.location_label} onChange={(e) => setEvent({ ...event, location_label: e.target.value })} />
                <button onClick={createEvent} disabled={busy === "event"} className="brutal-btn bg-primary text-primary-foreground px-3 py-2 w-full flex justify-center gap-2"><Plus size={14} /> Add event</button>
              </div>}
            </div>

            <div className="brutal-card bg-card p-4">
              <h3 className="text-xl flex items-center gap-2"><BookOpen size={18} /> Resources</h3>
              <div className="space-y-3 mt-3">
                {(hub.resources || []).length === 0 && <p className="text-sm text-muted-foreground">No shared resources yet.</p>}
                {(hub.resources || []).map((r) => <div key={r.id} className="border-2 border-foreground rounded-xl p-3">
                  <div className="text-[10px] uppercase font-bold opacity-60">{r.resource_type}</div>
                  <div className="font-bold">{r.title}</div>
                  {r.description && <p className="text-sm mt-1">{r.description}</p>}
                  {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-xs underline inline-flex gap-1 mt-2">Open resource <ExternalLink size={11} /></a>}
                </div>)}
              </div>
              {hub.is_owner && <div className="mt-4 space-y-2 border-t-2 border-foreground pt-3">
                <input className="brutal-input w-full px-3 py-2" placeholder="Resource title" value={resource.title} onChange={(e) => setResource({ ...resource, title: e.target.value })} />
                <select className="brutal-input w-full px-3 py-2" value={resource.resource_type} onChange={(e) => setResource({ ...resource, resource_type: e.target.value })}><option value="link">Link</option><option value="guide">Guide</option><option value="tool">Tool</option><option value="template">Template</option><option value="opportunity">Opportunity</option><option value="other">Other</option></select>
                <input className="brutal-input w-full px-3 py-2" placeholder="https://..." value={resource.url} onChange={(e) => setResource({ ...resource, url: e.target.value })} />
                <button onClick={createResource} disabled={busy === "resource"} className="brutal-btn bg-primary text-primary-foreground px-3 py-2 w-full flex justify-center gap-2"><Plus size={14} /> Add resource</button>
              </div>}
            </div>
          </div>
        </section>
      )}

      {comm.joined && <CommunityAiCard communityId={id} />}

      {comm.joined && <ComposePost onPosted={(p) => { if (mounted.current) setPosts((previous) => [p, ...previous]); }} communityId={id} />}

      <section>
        <h2 className="text-2xl mb-3">Conversation</h2>
        <div className="space-y-5">
          {posts.length === 0 ? <div className="brutal-card p-6 bg-card">No posts here yet.</div> : posts.map((p) => <PostCard key={p.id} post={p} />)}
        </div>
      </section>
    </div>
  );
}
